import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  BarChart3, PieChart as PieIcon, TrendingUp, Grid3X3, 
  Download, Filter, RefreshCw, FileSpreadsheet, FileText, Users, ChevronDown, ChevronUp, Eye, Trash2, MessageSquare, Sparkles, UserCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { AdminLayout } from './AdminDashboard';
import { surveyAPI, analyticsAPI, employeeAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis
} from 'recharts';

const Analytics = () => {
  const { isSuperAdmin, isSrManagement, hasRawDataAccess } = useAuth();
  const canViewResponses = hasRawDataAccess; // Super Admin always, Sr Mgmt only with raw_data_access flag
  const [surveys, setSurveys] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [responsesData, setResponsesData] = useState(null);
  const [expandedResponse, setExpandedResponse] = useState(null);
  const [filterDept, setFilterDept] = useState('');
  const [allDepartments, setAllDepartments] = useState([]);
  const [commentsData, setCommentsData] = useState(null);
  const [commentsView, setCommentsView] = useState('grouped'); // 'grouped' | 'flat'
  const [commentsSearch, setCommentsSearch] = useState('');
  const [selectedCommentQ, setSelectedCommentQ] = useState('__all__');
  const [analyzingAI, setAnalyzingAI] = useState(false);
  const [participationData, setParticipationData] = useState(null);
  const [participationSearch, setParticipationSearch] = useState('');
  const [sentimentFilter, setSentimentFilter] = useState(null); // null | 'positive' | 'neutral' | 'negative'

  const COLORS = ['#1B2A41', '#E07A5F', '#81B29A', '#F2CC8F', '#3D405B', '#9B59B6', '#3498DB', '#E74C3C'];

  useEffect(() => {
    loadSurveys();
    loadTrends();
    loadComparison();
    // Load department list from /employees/departments so filter has options even before any responses
    (async () => {
      try {
        const res = await employeeAPI.getDepartments();
        const depts = Array.isArray(res.data) ? res.data.filter(Boolean) : (res.data.departments || []).filter(Boolean);
        if (depts.length > 0) setAllDepartments(depts.sort());
      } catch (e) { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (selectedSurveyId) {
      loadAnalytics(selectedSurveyId, filterDept);
      loadHeatmap(selectedSurveyId, filterDept);
      if (responsesData) loadResponses(selectedSurveyId, filterDept);
      if (participationData) loadParticipation(selectedSurveyId, filterDept);
    }
  }, [selectedSurveyId, filterDept]);

  const loadSurveys = async () => {
    try {
      const response = await surveyAPI.getAll();
      setSurveys(response.data);
      if (response.data.length > 0) {
        setSelectedSurveyId(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async (surveyId, department) => {
    if (!surveyId) return;
    try {
      const params = department ? { department } : {};
      const response = await analyticsAPI.getSurveyAnalytics(surveyId, params);
      setAnalytics(response.data);
      if (!department && response.data.department_scores) {
        const depts = [...new Set(response.data.department_scores.map(d => d.department).filter(Boolean))];
        setAllDepartments(depts);
      }
    } catch (error) {
      console.error('Failed to load analytics');
    }
  };

  const loadHeatmap = async (surveyId, department) => {
    if (!surveyId) return;
    try {
      const params = department ? { department } : {};
      const response = await analyticsAPI.getHeatmap(surveyId, params);
      setHeatmapData(response.data);
    } catch (error) {
      console.error('Failed to load heatmap');
    }
  };

  const loadResponses = async (surveyId, department) => {    if (!surveyId) return;
    try {
      const params = department ? { department } : {};
      const response = await analyticsAPI.getIndividualResponses(surveyId, params);
      setResponsesData(response.data);
    } catch (error) {
      console.error('Failed to load responses');
      toast.error('Failed to load individual responses');
    }
  };

  const loadComments = async (surveyId) => {
    if (!surveyId) return;
    try {
      const response = await analyticsAPI.getComments(surveyId);
      setCommentsData(response.data);
    } catch (error) {
      console.error('Failed to load comments');
      toast.error('Failed to load comments');
    }
  };

  const loadParticipation = async (surveyId, department) => {
    if (!surveyId) return;
    try {
      const params = department ? { department } : {};
      const response = await analyticsAPI.getParticipation(surveyId, params);
      setParticipationData(response.data);
    } catch (error) {
      console.error('Failed to load participation');
      toast.error('Failed to load participation data');
    }
  };

  const handleAnalyzeAI = async () => {
    if (!selectedSurveyId) return;
    setAnalyzingAI(true);
    try {
      await analyticsAPI.analyzeComments(selectedSurveyId);
      toast.success('AI analysis complete');
      await loadComments(selectedSurveyId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI analysis failed');
    } finally {
      setAnalyzingAI(false);
    }
  };

  const handleExportComments = async (format) => {
    if (!selectedSurveyId) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/comments/${selectedSurveyId}/${format}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('auth_token')}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const extMap = { csv: 'csv', pdf: 'pdf', xlsx: 'xlsx' };
      a.download = `comments_${selectedSurveyId}.${extMap[format] || format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Comments exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error('Failed to export comments');
    }
  };

  const loadTrends = async () => {
    try {
      const response = await analyticsAPI.getTrends(6);
      setTrends(response.data.trends);
    } catch (error) {
      console.error('Failed to load trends');
    }
  };

  const handleExportRawData = async (format) => {
    if (!selectedSurveyId) {
      toast.error('Please select a survey first');
      return;
    }
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/export/survey/${selectedSurveyId}/raw?format=${format}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `survey_raw_data.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Raw data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleExportParticipation = async () => {
    if (!selectedSurveyId) {
      toast.error('Please select a survey first');
      return;
    }
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/export/participation/${selectedSurveyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `participation_report.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Participation report exported');
    } catch (error) {
      toast.error('Failed to export participation report');
    }
  };

  const handleExportAnalytics = async (format) => {
    if (!selectedSurveyId) {
      toast.error('Please select a survey first');
      return;
    }
    try {
      const token = sessionStorage.getItem('auth_token');
      const qs = filterDept ? `?department=${encodeURIComponent(filterDept)}` : '';
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/export/analytics/${selectedSurveyId}/${format}${qs}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeDept = (filterDept || 'all').replace(/[^a-z0-9_-]+/gi, '_');
      a.download = `analytics_report_${safeDept}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success(filterDept
        ? `Exported ${format.toUpperCase()} for ${filterDept}`
        : `Analytics report exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export analytics report');
    }
  };

  const loadComparison = async () => {
    try {
      const response = await analyticsAPI.getComparison();
      setComparison(response.data.comparison);
    } catch (error) {
      console.error('Failed to load comparison');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 4) return 'bg-accent text-white';
    if (score >= 3) return 'bg-chart-4 text-primary';
    if (score >= 2) return 'bg-secondary text-white';
    return 'bg-destructive text-white';
  };

  const getScoreLabel = (score, hasData) => {
    if (!hasData) return 'No Data';
    if (score >= 4) return 'Excellent';
    if (score >= 3) return 'Good';
    if (score >= 2) return 'Needs Attention';
    return 'Critical';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold mb-2">Analytics & Reports</h1>
            <p className="text-muted-foreground">Insights from employee motivation surveys</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedSurveyId || '__none__'} onValueChange={(v) => v !== '__none__' && setSelectedSurveyId(v)}>
              <SelectTrigger className="w-64" data-testid="survey-selector">
                <SelectValue placeholder="Select Survey" />
              </SelectTrigger>
              <SelectContent>
                {surveys.length === 0 ? (
                  <SelectItem value="__none__" disabled>No surveys available</SelectItem>
                ) : (
                  surveys.map((survey) => (
                    <SelectItem key={survey.id} value={survey.id}>
                      {survey.title}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <Select value={filterDept || '__all__'} onValueChange={(v) => setFilterDept(v === '__all__' ? '' : v)}>
              <SelectTrigger className="w-52" data-testid="global-dept-filter">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Departments</SelectItem>
                {allDepartments.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => { loadAnalytics(selectedSurveyId, filterDept); loadHeatmap(selectedSurveyId, filterDept); }}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            
            {/* Export Options */}
            {selectedSurveyId && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="export-btn">
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleExportAnalytics('pdf')} data-testid="export-analytics-pdf">
                    <Download className="h-4 w-4 mr-2" />
                    Analytics Report (PDF)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExportAnalytics('excel')} data-testid="export-analytics-excel">
                    <Download className="h-4 w-4 mr-2" />
                    Analytics Report (Excel)
                  </DropdownMenuItem>
                  {hasRawDataAccess && (
                    <>
                      <DropdownMenuItem onClick={() => handleExportRawData('xlsx')} data-testid="export-xlsx">
                        <Download className="h-4 w-4 mr-2" />
                        Raw Data (Excel)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportRawData('csv')} data-testid="export-csv">
                        <Download className="h-4 w-4 mr-2" />
                        Raw Data (CSV)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleExportParticipation} data-testid="export-participation">
                        <Download className="h-4 w-4 mr-2" />
                        Participation Report
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {!analytics ? (
          <Card className="text-center py-12">
            <CardContent>
              <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-lg font-medium mb-2">No Data Available</h3>
              <p className="text-muted-foreground">Select a survey to view analytics</p>
            </CardContent>
          </Card>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="overview" data-testid="tab-overview">
                <BarChart3 className="h-4 w-4 mr-2" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="heatmap" data-testid="tab-heatmap">
                <Grid3X3 className="h-4 w-4 mr-2" />
                Heat Map
              </TabsTrigger>
              <TabsTrigger value="trends" data-testid="tab-trends">
                <TrendingUp className="h-4 w-4 mr-2" />
                Trends
              </TabsTrigger>
              <TabsTrigger value="comments" data-testid="tab-comments" onClick={() => { if (!commentsData) loadComments(selectedSurveyId); }}>
                <MessageSquare className="h-4 w-4 mr-2" />
                Open Comments
              </TabsTrigger>
              {hasRawDataAccess && (
                <TabsTrigger value="participation" data-testid="tab-participation" onClick={() => { if (!participationData) loadParticipation(selectedSurveyId, filterDept); }}>
                  <UserCheck className="h-4 w-4 mr-2" />
                  Participation
                </TabsTrigger>
              )}
              {canViewResponses && (
                <TabsTrigger value="responses" data-testid="tab-responses" onClick={() => { if (!responsesData) loadResponses(selectedSurveyId); }}>
                  <Users className="h-4 w-4 mr-2" />
                  Responses
                </TabsTrigger>
              )}
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              {/* Scoring Legend */}
              <Card className="mb-6 bg-muted/30" data-testid="scoring-legend">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <span className="font-medium text-muted-foreground">Scoring Guide:</span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-green-500"></span>
                      <span><strong>4.5–5.0</strong> Excellent</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-emerald-400"></span>
                      <span><strong>3.5–4.5</strong> Good</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                      <span><strong>2.5–3.5</strong> Average</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-red-500"></span>
                      <span><strong>&lt; 2.5</strong> Critical</span>
                    </span>
                    {filterDept && (
                      <Badge variant="secondary" className="ml-auto" data-testid="active-dept-filter-badge">
                        Filtered: {filterDept}
                      </Badge>
                    )}
                  </div>
                  {analytics.hidden_dept_count > 0 && (
                    <div className="mt-3 pt-3 border-t flex items-start gap-2 text-xs text-muted-foreground" data-testid="threshold-notice">
                      <span className="inline-block w-4 h-4 rounded-full bg-amber-500/20 text-amber-700 text-[10px] flex items-center justify-center font-bold shrink-0 mt-0.5">!</span>
                      <span>
                        <strong>{analytics.hidden_dept_count}</strong> department{analytics.hidden_dept_count > 1 ? 's' : ''} hidden — only departments with at least <strong>{analytics.min_dept_threshold}</strong> responses are shown to protect individual employee anonymity in small teams.
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Composite Scores Card */}
              {analytics.composite_scores && analytics.composite_scores.length > 0 && (
                <Card className="mb-6" data-testid="composite-scores-card">
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Grid3X3 className="h-5 w-5" />
                      Composite Group Scores
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      Weighted mean of grouped sections
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {analytics.composite_scores.map((g, gi) => (
                        <div key={gi} className="border rounded-xl p-4 bg-muted/30" data-testid={`composite-${gi}`}>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{g.name}</p>
                          <div className="flex items-baseline gap-2">
                            <span className="font-heading text-3xl font-bold">{g.avg_score.toFixed(1)}</span>
                            <span className="text-sm text-muted-foreground">/ 5.0</span>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full inline-block mt-1 ${getScoreColor(g.avg_score)}`}>
                            {getScoreLabel(g.avg_score, true)}
                          </span>
                          <div className="mt-2 text-xs text-muted-foreground">
                            {g.sections.length} section{g.sections.length !== 1 ? 's' : ''}: {g.sections.map(s => s.section).join(', ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <Card data-testid="stat-participation">
                  <CardContent className="p-6">
                    <p className="text-sm text-muted-foreground mb-1">Participation Rate</p>
                    <p className="font-heading text-3xl font-bold text-primary">
                      {analytics.participation_rate}%
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {analytics.total_responses} of {analytics.total_employees} employees
                    </p>
                  </CardContent>
                </Card>

                <Card data-testid="stat-avg-score">
                  <CardContent className="p-6">
                    <p className="text-sm text-muted-foreground mb-1">Overall Score</p>
                    <div className="flex items-center gap-2">
                      <p className="font-heading text-3xl font-bold">
                        {analytics.total_responses > 0 ? analytics.overall_avg_score.toFixed(1) : 'N/A'}
                      </p>
                      {analytics.total_responses > 0 && <span className="text-lg text-muted-foreground">/ 5</span>}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${analytics.total_responses > 0 ? getScoreColor(analytics.overall_avg_score) : 'bg-muted text-muted-foreground'}`}>
                      {getScoreLabel(analytics.overall_avg_score, analytics.total_responses > 0)}
                    </span>
                  </CardContent>
                </Card>

                <Card data-testid="stat-responses">
                  <CardContent className="p-6">
                    <p className="text-sm text-muted-foreground mb-1">Total Responses</p>
                    <p className="font-heading text-3xl font-bold">{analytics.total_responses}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {analytics.department_scores.length} departments
                    </p>
                  </CardContent>
                </Card>

                <Card data-testid="stat-questions">
                  <CardContent className="p-6">
                    <p className="text-sm text-muted-foreground mb-1">Questions Analyzed</p>
                    <p className="font-heading text-3xl font-bold">{analytics.question_scores.length}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      In this survey
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Department Scores */}
                <Card data-testid="chart-departments">
                  <CardHeader>
                    <CardTitle className="font-heading">Department Scores</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.department_scores} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" domain={[0, 5]} />
                          <YAxis dataKey="department" type="category" width={100} />
                          <Tooltip 
                            formatter={(value) => [value.toFixed(2), 'Score']}
                            contentStyle={{ borderRadius: '8px' }}
                          />
                          <Bar dataKey="avg_score" fill="#1B2A41" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Response Distribution — Donut Chart (Top 6 + Others) */}
                <Card data-testid="chart-distribution">
                  <CardHeader>
                    <CardTitle className="font-heading">Response Distribution by Department</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">Share of total responses</p>
                  </CardHeader>
                  <CardContent>
                    <div className="relative h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={(() => {
                              const sorted = [...analytics.department_scores].sort((a, b) => b.response_count - a.response_count);
                              if (sorted.length <= 7) return sorted;
                              const top = sorted.slice(0, 6);
                              const othersCount = sorted.slice(6).reduce((s, d) => s + d.response_count, 0);
                              return [...top, { department: 'Others', response_count: othersCount }];
                            })()}
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={95}
                            paddingAngle={2}
                            dataKey="response_count"
                            nameKey="department"
                          >
                            {analytics.department_scores.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={2} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value, name) => [`${value} responses`, name]}
                            contentStyle={{ borderRadius: '8px' }}
                          />
                          <Legend
                            layout="vertical"
                            align="right"
                            verticalAlign="middle"
                            iconType="circle"
                            formatter={(value, entry) => {
                              const total = analytics.total_responses || 1;
                              const pct = ((entry.payload.response_count / total) * 100).toFixed(0);
                              return `${value} (${pct}%)`;
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ marginRight: '38%' }}>
                        <p className="text-3xl font-heading font-bold text-foreground">{analytics.total_responses}</p>
                        <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Responses</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* eNPS Card */}
              {analytics.enps && analytics.enps.total_ratings > 0 && (
                <Card className="mb-8" data-testid="enps-card">
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Employee Net Promoter Score (eNPS)
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      Promoters (5) − Detractors (1-3). Higher is better. Above 0 is good; above 30 is excellent.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-stretch">
                      <div className={`rounded-xl p-5 flex flex-col justify-center ${analytics.enps.score >= 30 ? 'bg-emerald-50 border border-emerald-200' : analytics.enps.score >= 0 ? 'bg-amber-50 border border-amber-200' : 'bg-rose-50 border border-rose-200'}`}>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">eNPS Score</p>
                        <p className={`font-heading text-5xl font-bold mt-1 ${analytics.enps.score >= 30 ? 'text-emerald-700' : analytics.enps.score >= 0 ? 'text-amber-700' : 'text-rose-700'}`}>
                          {analytics.enps.score > 0 ? '+' : ''}{analytics.enps.score}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">{analytics.enps.total_ratings} ratings analyzed</p>
                      </div>
                      <div className="rounded-xl p-5 bg-emerald-50 border border-emerald-100">
                        <p className="text-xs text-emerald-700 font-semibold uppercase tracking-wider">Promoters</p>
                        <p className="font-heading text-3xl font-bold text-emerald-700 mt-1">{analytics.enps.promoter_pct}%</p>
                        <p className="text-xs text-muted-foreground mt-1">{analytics.enps.promoters} ratings (5/5)</p>
                      </div>
                      <div className="rounded-xl p-5 bg-amber-50 border border-amber-100">
                        <p className="text-xs text-amber-700 font-semibold uppercase tracking-wider">Passives</p>
                        <p className="font-heading text-3xl font-bold text-amber-700 mt-1">{analytics.enps.passive_pct}%</p>
                        <p className="text-xs text-muted-foreground mt-1">{analytics.enps.passives} ratings (4/5)</p>
                      </div>
                      <div className="rounded-xl p-5 bg-rose-50 border border-rose-100">
                        <p className="text-xs text-rose-700 font-semibold uppercase tracking-wider">Detractors</p>
                        <p className="font-heading text-3xl font-bold text-rose-700 mt-1">{analytics.enps.detractor_pct}%</p>
                        <p className="text-xs text-muted-foreground mt-1">{analytics.enps.detractors} ratings (1-3/5)</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Section Radar + Top 5 Strengths/Concerns Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Section Score Radar */}
                {analytics.section_scores && analytics.section_scores.length >= 3 && (
                  <Card data-testid="section-radar">
                    <CardHeader>
                      <CardTitle className="font-heading">Section Scores Overview</CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">Strengths & weak spots across every section at a glance</p>
                    </CardHeader>
                    <CardContent>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart data={analytics.section_scores.map(s => ({ section: s.section.length > 22 ? s.section.slice(0, 22) + '…' : s.section, fullName: s.section, avg_score: s.avg_score }))}>
                            <PolarGrid stroke="#e5e7eb" />
                            <PolarAngleAxis dataKey="section" tick={{ fontSize: 10, fill: '#475569' }} />
                            <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 9 }} />
                            <Radar name="Avg Score" dataKey="avg_score" stroke="#1B2A41" fill="#1B2A41" fillOpacity={0.35} />
                            <Tooltip
                              formatter={(value, name, props) => [value.toFixed(2), 'Score']}
                              labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                              contentStyle={{ borderRadius: '8px' }}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Top 5 Strengths */}
                {analytics.top_strengths && analytics.top_strengths.length > 0 && (
                  <Card data-testid="top-strengths-card" className="border-emerald-200">
                    <CardHeader>
                      <CardTitle className="font-heading flex items-center gap-2 text-emerald-700">
                        <Sparkles className="h-5 w-5" />
                        Top 5 Strengths
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">Highest-scoring questions — celebrate these</p>
                    </CardHeader>
                    <CardContent>
                      <ol className="space-y-3">
                        {analytics.top_strengths.map((q, i) => (
                          <li key={i} className="flex items-start gap-3 p-3 bg-emerald-50/60 rounded-lg" data-testid={`strength-${i}`}>
                            <span className="flex items-center justify-center h-7 w-7 shrink-0 rounded-full bg-emerald-600 text-white text-xs font-bold">{i + 1}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-foreground leading-snug">{q.text}</p>
                              <p className="text-[11px] text-muted-foreground mt-0.5">{q.section} · {q.response_count} responses</p>
                            </div>
                            <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white">{q.avg_score.toFixed(1)}</Badge>
                          </li>
                        ))}
                      </ol>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Top 5 Concerns (full-width) */}
              {analytics.top_concerns && analytics.top_concerns.length > 0 && (
                <Card className="mb-8 border-rose-200" data-testid="top-concerns-card">
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2 text-rose-700">
                      <TrendingUp className="h-5 w-5 rotate-180" />
                      Top 5 Concerns — Action Required
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">Lowest-scoring questions — prioritize action planning here</p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {analytics.top_concerns.map((q, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-rose-50/60 rounded-lg border border-rose-100" data-testid={`concern-${i}`}>
                          <span className="flex items-center justify-center h-7 w-7 shrink-0 rounded-full bg-rose-600 text-white text-xs font-bold">{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground leading-snug">{q.text}</p>
                            <p className="text-[11px] text-muted-foreground mt-0.5">{q.section} · {q.response_count} responses</p>
                          </div>
                          <Badge className="bg-rose-600 hover:bg-rose-600 text-white">{q.avg_score.toFixed(1)}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Question Analysis */}
              <Card data-testid="question-analysis">
                <CardHeader>
                  <CardTitle className="font-heading">Question-wise Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analytics.question_scores.map((q, index) => (
                      <div 
                        key={q.question_id}
                        className="flex flex-col gap-3 p-4 bg-muted/50 rounded-xl"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary/10 text-primary text-sm font-bold">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{q.question_text}</p>
                            <p className="text-xs text-muted-foreground capitalize">
                              {q.question_type.replace('_', ' ')} • {q.response_count} responses
                            </p>
                          </div>
                          <div className="text-right">
                            {q.is_informational ? (
                              <div className="flex flex-col items-end" data-testid={`informational-${q.question_id}`}>
                                <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full flex items-center gap-1">
                                  <MessageSquare className="h-3 w-3" />
                                  Informational
                                </span>
                                <p className="text-xs text-muted-foreground mt-1">No score — see Responses tab</p>
                              </div>
                            ) : (
                              <>
                                <p className="font-heading text-xl font-bold">{q.avg_score.toFixed(1)}</p>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${getScoreColor(q.avg_score)}`}>
                                  {getScoreLabel(q.avg_score, true)}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        {q.question_type === 'multi_select' && q.option_distribution && q.option_distribution.length > 0 && (
                          <div className="bg-background rounded-lg p-3" data-testid={`multi-distribution-${q.question_id}`}>
                            <p className="text-xs font-medium text-muted-foreground mb-2">Selection breakdown</p>
                            <ResponsiveContainer width="100%" height={Math.max(140, q.option_distribution.length * 32)}>
                              <BarChart
                                data={q.option_distribution.map(o => ({ name: o.text, count: o.count, score: o.score }))}
                                layout="vertical"
                                margin={{ left: 10, right: 20, top: 0, bottom: 0 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" allowDecimals={false} />
                                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                                <Tooltip formatter={(value, name, props) => [`${value} pick${value === 1 ? '' : 's'} (weight ${props.payload.score})`, 'Selections']} />
                                <Bar dataKey="count" fill="#E07A5F" radius={[0, 4, 4, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Heat Map Tab */}
            <TabsContent value="heatmap">
              <Card data-testid="heatmap-view">
                <CardHeader>
                  <CardTitle className="font-heading">Department vs Question Heat Map</CardTitle>
                </CardHeader>
                <CardContent>
                  {heatmapData && heatmapData.heatmap.length > 0 ? (
                    <div className="overflow-x-auto">
                      <div className="min-w-[800px]">
                        {/* Header Row */}
                        <div className="flex items-center mb-4">
                          <div className="w-32 flex-shrink-0"></div>
                          {heatmapData.questions.filter(q => q.type === 'rating').map((q, idx) => (
                            <div 
                              key={q.id} 
                              className="flex-1 px-2 text-center"
                              title={q.text}
                            >
                              <span className="text-xs font-medium text-muted-foreground truncate block">
                                Q{idx + 1}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Data Rows */}
                        {heatmapData.heatmap.map((row) => (
                          <div key={row.department} className="flex items-center mb-2">
                            <div className="w-32 flex-shrink-0 pr-4">
                              <span className="text-sm font-medium truncate block">
                                {row.department}
                              </span>
                            </div>
                            {Object.entries(row.questions).map(([qId, data]) => (
                              <div key={qId} className="flex-1 px-1">
                                <div 
                                  className={`
                                    heatmap-cell rounded-lg p-3 text-center cursor-pointer
                                    ${getScoreColor(data.avg_score)}
                                  `}
                                  title={`${data.text}: ${data.avg_score.toFixed(2)} (${data.count} responses)`}
                                >
                                  <span className="font-bold">{data.avg_score.toFixed(1)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}

                        {/* Legend */}
                        <div className="flex items-center justify-center gap-4 mt-6 pt-4 border-t">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-destructive"></div>
                            <span className="text-xs">Critical (&lt;2)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-secondary"></div>
                            <span className="text-xs">Needs Attention (2-3)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-chart-4"></div>
                            <span className="text-xs">Good (3-4)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-accent"></div>
                            <span className="text-xs">Excellent (4+)</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      No heatmap data available for this survey
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Trends Tab */}
            <TabsContent value="trends">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Survey Trends */}
                <Card data-testid="chart-trends">
                  <CardHeader>
                    <CardTitle className="font-heading">Survey Score Trends</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      {trends && trends.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={trends}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="date" 
                              tickFormatter={(val) => new Date(val).toLocaleDateString('en', { month: 'short' })}
                            />
                            <YAxis domain={[0, 5]} />
                            <Tooltip 
                              labelFormatter={(val) => new Date(val).toLocaleDateString()}
                              formatter={(value) => [value.toFixed(2), 'Avg Score']}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="avg_score" 
                              stroke="#E07A5F" 
                              strokeWidth={2}
                              dot={{ fill: '#E07A5F' }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="flex items-center justify-center h-full text-muted-foreground">
                          Not enough data for trends
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Quarter Comparison */}
                <Card data-testid="chart-comparison">
                  <CardHeader>
                    <CardTitle className="font-heading">Quarter-over-Quarter Comparison</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      {comparison && comparison.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={comparison}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="quarter" />
                            <YAxis domain={[0, 5]} />
                            <Tooltip formatter={(value) => [value.toFixed(2), 'Avg Score']} />
                            <Bar dataKey="avg_score" fill="#1B2A41" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="flex items-center justify-center h-full text-muted-foreground">
                          Not enough data for comparison
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Open-Ended Comments Tab */}
            <TabsContent value="comments">
              <Card data-testid="comments-tab-content">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <CardTitle className="font-heading flex items-center gap-2">
                        <MessageSquare className="h-5 w-5" />
                        Open-Ended Comments
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">
                        Fully anonymous — no employee or department identifiers attached
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {!isSrManagement && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={handleAnalyzeAI}
                        disabled={analyzingAI}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                        data-testid="analyze-ai-btn"
                      >
                        <Sparkles className="h-4 w-4 mr-2" />
                        {analyzingAI ? 'Analyzing...' : (commentsData?.sentiment_summary ? 'Re-analyze with AI' : 'Analyze with AI')}
                      </Button>
                      )}
                      <Button
                        variant={commentsView === 'grouped' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCommentsView('grouped')}
                        data-testid="comments-view-grouped"
                      >
                        Grouped
                      </Button>
                      <Button
                        variant={commentsView === 'flat' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCommentsView('flat')}
                        data-testid="comments-view-flat"
                      >
                        Flat List
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" data-testid="comments-export-btn">
                            <Download className="h-4 w-4 mr-2" />
                            Export
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleExportComments('xlsx')}>
                            <FileSpreadsheet className="h-4 w-4 mr-2" />
                            Excel (XLSX) - best for हिं/ગુ/मरा
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleExportComments('csv')}>
                            <FileSpreadsheet className="h-4 w-4 mr-2" />
                            CSV
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleExportComments('pdf')}>
                            <FileText className="h-4 w-4 mr-2" />
                            PDF
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {!commentsData ? (
                    <div className="text-center py-12 text-muted-foreground">Loading comments...</div>
                  ) : (commentsData.questions || []).length === 0 ? (
                    <div className="text-center py-12">
                      <MessageSquare className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
                      <p className="text-muted-foreground">This survey has no open-ended questions.</p>
                    </div>
                  ) : (
                    <>
                      {/* AI Sentiment Summary */}
                      {commentsData.sentiment_summary && (
                        <div className="mb-6 border rounded-xl p-4 bg-gradient-to-br from-purple-50 to-pink-50" data-testid="sentiment-summary-card">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-heading text-base font-semibold flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-purple-600" />
                                AI Sentiment & Themes
                              </h3>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {commentsData.sentiment_summary.total_analyzed} comments analyzed
                              </p>
                            </div>
                          </div>
                          {(() => {
                            const s = commentsData.sentiment_summary;
                            const total = (s.positive || 0) + (s.neutral || 0) + (s.negative || 0) || 1;
                            const pct = (n) => Math.round((n / total) * 100);
                            return (
                              <>
                                <div className="grid grid-cols-3 gap-3 mb-4">
                                  <button
                                    type="button"
                                    onClick={() => setSentimentFilter('positive')}
                                    className="bg-white/70 rounded-lg p-3 text-center hover:bg-white hover:shadow-md transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-400"
                                    data-testid="sentiment-positive"
                                    title="Click to view positive comments"
                                  >
                                    <p className="text-2xl font-bold text-green-600">{pct(s.positive)}%</p>
                                    <p className="text-xs text-muted-foreground mt-1">Positive ({s.positive})</p>
                                    <p className="text-[10px] text-green-700 mt-1 font-medium">View list →</p>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setSentimentFilter('neutral')}
                                    className="bg-white/70 rounded-lg p-3 text-center hover:bg-white hover:shadow-md transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400"
                                    data-testid="sentiment-neutral"
                                    title="Click to view neutral comments"
                                  >
                                    <p className="text-2xl font-bold text-amber-600">{pct(s.neutral)}%</p>
                                    <p className="text-xs text-muted-foreground mt-1">Neutral ({s.neutral})</p>
                                    <p className="text-[10px] text-amber-700 mt-1 font-medium">View list →</p>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setSentimentFilter('negative')}
                                    className="bg-white/70 rounded-lg p-3 text-center hover:bg-white hover:shadow-md transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-400"
                                    data-testid="sentiment-negative"
                                    title="Click to view negative comments"
                                  >
                                    <p className="text-2xl font-bold text-red-600">{pct(s.negative)}%</p>
                                    <p className="text-xs text-muted-foreground mt-1">Negative ({s.negative})</p>
                                    <p className="text-[10px] text-red-700 mt-1 font-medium">View list →</p>
                                  </button>
                                </div>
                                {(s.themes || []).length > 0 && (
                                  <div data-testid="themes-list">
                                    <p className="text-xs font-medium text-muted-foreground mb-2">Top Themes</p>
                                    <div className="flex flex-wrap gap-2">
                                      {s.themes.map((t, ti) => (
                                        <span key={ti} className="bg-white/80 border border-purple-200 text-purple-800 text-xs px-3 py-1 rounded-full">
                                          {t.theme} {t.count ? <span className="text-muted-foreground">({t.count})</span> : null}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}

                      {/* Search + Question filter (only for flat view) */}
                      <div className="flex flex-col sm:flex-row gap-3 mb-6">
                        <Input
                          placeholder="Search comments..."
                          value={commentsSearch}
                          onChange={(e) => setCommentsSearch(e.target.value)}
                          className="flex-1"
                          data-testid="comments-search"
                        />
                        {commentsView === 'flat' && (
                          <Select value={selectedCommentQ} onValueChange={setSelectedCommentQ}>
                            <SelectTrigger className="w-64" data-testid="comments-question-filter">
                              <SelectValue placeholder="All questions" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__all__">All questions</SelectItem>
                              {(commentsData.questions || []).map((q) => (
                                <SelectItem key={q.question_id} value={q.question_id}>
                                  Q{q.question_number}: {q.question_text.length > 50 ? q.question_text.substring(0, 50) + '...' : q.question_text}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>

                      {commentsView === 'grouped' ? (
                        /* Grouped View */
                        <div className="space-y-6" data-testid="comments-grouped-list">
                          {(commentsData.questions || []).map((q) => {
                            const filtered = (q.comments || []).filter((c) =>
                              !commentsSearch || c.toLowerCase().includes(commentsSearch.toLowerCase())
                            );
                            return (
                              <div key={q.question_id} className="border rounded-xl overflow-hidden">
                                <div className="bg-muted/50 p-4 border-b flex items-start gap-3">
                                  <span className="font-heading font-bold text-primary bg-primary/10 rounded-lg px-2 py-1 text-sm shrink-0" data-testid={`q-number-${q.question_number}`}>
                                    Q{q.question_number}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium">{q.question_text}</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {filtered.length} of {q.comment_count} comment(s)
                                      {commentsSearch && ` matching "${commentsSearch}"`}
                                    </p>
                                  </div>
                                </div>
                                <div className="divide-y">
                                  {filtered.length === 0 ? (
                                    <p className="p-4 text-sm text-muted-foreground italic">
                                      {commentsSearch ? 'No matching comments.' : 'No comments yet.'}
                                    </p>
                                  ) : (
                                    filtered.map((c, i) => {
                                      const enriched = (q.comments_enriched || []).find(e => e.text === c);
                                      const sent = enriched?.sentiment;
                                      const sentColor = sent === 'positive' ? 'bg-green-100 text-green-700' : sent === 'negative' ? 'bg-red-100 text-red-700' : sent === 'neutral' ? 'bg-amber-100 text-amber-700' : '';
                                      return (
                                      <div key={i} className="p-4 flex items-start gap-3 hover:bg-muted/30 transition-colors" data-testid={`comment-${q.question_id}-${i}`}>
                                        <span className="text-xs font-bold text-muted-foreground bg-muted rounded-full h-6 w-6 flex items-center justify-center shrink-0">
                                          {i + 1}
                                        </span>
                                        <p className="text-sm leading-relaxed flex-1">{c}</p>
                                        {sent && (
                                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sentColor}`} data-testid={`sentiment-chip-${q.question_id}-${i}`}>
                                            {sent}
                                          </span>
                                        )}
                                      </div>
                                    );})
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        /* Flat View */
                        <div className="space-y-2" data-testid="comments-flat-list">
                          {(() => {
                            const allComments = [];
                            (commentsData.questions || []).forEach((q) => {
                              if (selectedCommentQ !== '__all__' && q.question_id !== selectedCommentQ) return;
                              (q.comments || []).forEach((c) => {
                                if (!commentsSearch || c.toLowerCase().includes(commentsSearch.toLowerCase())) {
                                  allComments.push({ text: c, question: q.question_text, qNumber: q.question_number, qid: q.question_id });
                                }
                              });
                            });
                            if (allComments.length === 0) {
                              return <p className="text-center py-8 text-muted-foreground italic">No comments to display.</p>;
                            }
                            return allComments.map((entry, i) => (
                              <div key={i} className="p-3 border rounded-lg hover:bg-muted/30 transition-colors flex items-start gap-3" data-testid={`flat-comment-${i}`}>
                                <span className="text-xs font-bold text-primary bg-primary/10 rounded px-1.5 py-0.5 shrink-0">
                                  Q{entry.qNumber}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-muted-foreground mb-1 line-clamp-1">{entry.question}</p>
                                  <p className="text-sm">{entry.text}</p>
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Participation Tab */}
            <TabsContent value="participation">
              {!participationData ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <UserCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading participation data...</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6" data-testid="participation-view">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card data-testid="participation-total">
                      <CardContent className="p-6">
                        <p className="text-sm text-muted-foreground mb-1">Active Employees</p>
                        <p className="font-heading text-3xl font-bold text-primary">{participationData.total_employees}</p>
                        <p className="text-xs text-muted-foreground mt-1">in employee master</p>
                      </CardContent>
                    </Card>
                    <Card data-testid="participation-completed">
                      <CardContent className="p-6">
                        <p className="text-sm text-muted-foreground mb-1">Completed</p>
                        <p className="font-heading text-3xl font-bold text-green-600">{participationData.completed_count}</p>
                        {participationData.former_count > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">+{participationData.former_count} former employee(s)</p>
                        )}
                      </CardContent>
                    </Card>
                    <Card data-testid="participation-pending">
                      <CardContent className="p-6">
                        <p className="text-sm text-muted-foreground mb-1">Pending</p>
                        <p className="font-heading text-3xl font-bold text-red-500">{participationData.pending_count}</p>
                        <p className="text-xs text-muted-foreground mt-1">not yet submitted</p>
                      </CardContent>
                    </Card>
                    <Card data-testid="participation-rate">
                      <CardContent className="p-6">
                        <p className="text-sm text-muted-foreground mb-1">Completion Rate</p>
                        <p className="font-heading text-3xl font-bold">{participationData.completion_rate}%</p>
                        <p className="text-xs text-muted-foreground mt-1">of active employees</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Search */}
                  <div className="flex items-center gap-3">
                    <Input
                      placeholder="Search by name, ID or department..."
                      value={participationSearch}
                      onChange={(e) => setParticipationSearch(e.target.value)}
                      className="max-w-sm"
                      data-testid="participation-search"
                    />
                    <Button variant="outline" size="sm" onClick={() => loadParticipation(selectedSurveyId, filterDept)}>
                      <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                    </Button>
                  </div>

                  {/* Employee Status Table */}
                  <Card>
                    <CardContent className="p-0">
                      <Table data-testid="participation-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Employee ID</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Department</TableHead>
                            <TableHead>Designation</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Submitted</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(() => {
                            const q = participationSearch.toLowerCase();
                            const match = (e) => !q || (e.name || '').toLowerCase().includes(q) || (e.employee_id || '').toLowerCase().includes(q) || (e.department || '').toLowerCase().includes(q);
                            const rows = [
                              ...(participationData.completed || []).filter(match).map((e) => ({ ...e, _status: 'completed' })),
                              ...(participationData.pending || []).filter(match).map((e) => ({ ...e, _status: 'pending' })),
                            ];
                            if (rows.length === 0) {
                              return (
                                <TableRow>
                                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                    No employees match your search
                                  </TableCell>
                                </TableRow>
                              );
                            }
                            return rows.map((e, i) => (
                              <TableRow key={`${e.employee_id}-${i}`} data-testid={`participation-row-${e.employee_id}`}>
                                <TableCell className="font-mono text-sm">{e.employee_id}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{e.name}</span>
                                    {e.is_former_employee && (
                                      <Badge variant="outline" className="text-amber-600 border-amber-300 text-[10px]">Former</Badge>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell><Badge variant="outline">{e.department}</Badge></TableCell>
                                <TableCell className="text-sm text-muted-foreground">{e.designation}</TableCell>
                                <TableCell>
                                  {e._status === 'completed' ? (
                                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Completed</Badge>
                                  ) : (
                                    <Badge variant="destructive" className="bg-red-100 text-red-600 hover:bg-red-100">Pending</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {e.submitted_at ? new Date(e.submitted_at).toLocaleDateString() : '-'}
                                </TableCell>
                              </TableRow>
                            ));
                          })()}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* Individual Responses Tab */}
            <TabsContent value="responses">
              <div className="space-y-6">
                {/* Filter Bar */}
                <div className="flex items-center gap-3">
                  <Select value={filterDept || '__all__'} onValueChange={(v) => { const dept = v === '__all__' ? '' : v; setFilterDept(dept); loadResponses(selectedSurveyId, dept); }}>
                    <SelectTrigger className="w-48" data-testid="dept-filter">
                      <SelectValue placeholder="All Departments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Departments</SelectItem>
                      {analytics?.department_scores?.filter(d => d.department).map((d) => (
                        <SelectItem key={d.department} value={d.department}>{d.department}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={() => loadResponses(selectedSurveyId, filterDept)}>
                    <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                  </Button>
                  {responsesData && (
                    <Badge variant="outline" className="ml-auto">{responsesData.total_responses} responses</Badge>
                  )}
                </div>

                {!responsesData ? (
                  <Card className="text-center py-12">
                    <CardContent>
                      <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Loading responses...</p>
                    </CardContent>
                  </Card>
                ) : responsesData.responses.length === 0 ? (
                  <Card className="text-center py-12">
                    <CardContent>
                      <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-heading text-lg font-medium mb-2">No Responses Yet</h3>
                      <p className="text-muted-foreground">No employees have completed this survey</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10">#</TableHead>
                            <TableHead>Employee</TableHead>
                            <TableHead>Department</TableHead>
                            <TableHead>Avg Score</TableHead>
                            <TableHead>Submitted</TableHead>
                            <TableHead className="text-right">Details</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {responsesData.responses.map((resp, idx) => (
                            <React.Fragment key={`resp-${idx}`}>
                              <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => setExpandedResponse(expandedResponse === idx ? null : idx)} data-testid={`response-row-${idx}`}>
                                <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                                <TableCell>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium">{resp.employee_name}</p>
                                      {resp.is_former_employee && (
                                        <Badge variant="outline" className="text-amber-600 border-amber-300 text-[10px]">Former</Badge>
                                      )}
                                    </div>
                                    <p className="text-xs text-muted-foreground">{resp.employee_id}</p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline">{resp.department}</Badge>
                                </TableCell>
                                <TableCell>
                                  <span className={`font-semibold ${resp.average_score >= 4 ? 'text-green-600' : resp.average_score >= 3 ? 'text-amber-500' : 'text-red-500'}`}>
                                    {resp.average_score.toFixed(1)} / 5
                                  </span>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {resp.submitted_at ? new Date(resp.submitted_at).toLocaleDateString() : '-'}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    {isSuperAdmin && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          if (!window.confirm(`Delete response from ${resp.employee_name || resp.employee_id}? This cannot be undone.`)) return;
                                          try {
                                            await surveyAPI.deleteResponse(selectedSurveyId, resp.employee_id);
                                            toast.success('Response deleted');
                                            loadResponses(selectedSurveyId, filterDept);
                                            // Refresh analytics counters
                                            const a = await analyticsAPI.getSurveyAnalytics(selectedSurveyId);
                                            setAnalytics(a.data);
                                          } catch (err) {
                                            toast.error(err.response?.data?.detail || 'Failed to delete response');
                                          }
                                        }}
                                        data-testid={`delete-response-${idx}`}
                                        title="Delete this response"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                    <Button variant="ghost" size="icon">
                                      {expandedResponse === idx ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                              {expandedResponse === idx && (
                                <TableRow key={`detail-${idx}`}>
                                  <TableCell colSpan={6} className="bg-muted/30 p-0">
                                    <div className="p-4 space-y-3">
                                      {(() => {
                                        const sections = {};
                                        resp.answers.forEach(a => {
                                          const sec = a.section || 'General';
                                          if (!sections[sec]) sections[sec] = [];
                                          sections[sec].push(a);
                                        });
                                        return Object.entries(sections).map(([section, answers]) => (
                                          <div key={section}>
                                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{section}</p>
                                            <div className="space-y-2">
                                              {answers.map((a, ai) => (
                                                <div key={ai} className="flex items-start justify-between gap-4 bg-background rounded-lg px-3 py-2 border">
                                                  <p className="text-sm flex-1">{a.question_text}</p>
                                                  <div className="shrink-0">
                                                    {a.question_type === 'rating' ? (
                                                      <div className="flex items-center gap-1">
                                                        {[1,2,3,4,5].map(star => (
                                                          <div key={star} className={`w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold ${a.answer >= star ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                                                            {star}
                                                          </div>
                                                        ))}
                                                      </div>
                                                    ) : a.question_type === 'yes_no' ? (
                                                      <Badge variant={String(a.answer).toLowerCase() === 'yes' ? 'default' : 'destructive'}>
                                                        {String(a.answer).toLowerCase() === 'yes' ? 'Yes' : 'No'}
                                                      </Badge>
                                                    ) : a.question_type === 'multi_select' ? (
                                                      <div className="flex flex-wrap gap-1 justify-end max-w-xs">
                                                        {Array.isArray(a.answer) && a.answer.length > 0 ? (
                                                          a.answer.map((val, vi) => {
                                                            const opt = (a.options || []).find(o => o.value === val);
                                                            return (
                                                              <Badge key={vi} variant="secondary" className="text-xs">
                                                                {opt ? opt.text : val}
                                                              </Badge>
                                                            );
                                                          })
                                                        ) : (
                                                          <span className="text-xs text-muted-foreground italic">No selection</span>
                                                        )}
                                                      </div>
                                                    ) : (
                                                      <p className="text-sm text-muted-foreground italic max-w-xs truncate">{a.answer || '-'}</p>
                                                    )}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        ));
                                      })()}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* Sentiment Comments Dialog — opens when user clicks a sentiment percentage card */}
        <Dialog open={!!sentimentFilter} onOpenChange={(open) => !open && setSentimentFilter(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="sentiment-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2 capitalize">
                <Sparkles className={`h-5 w-5 ${sentimentFilter === 'positive' ? 'text-green-600' : sentimentFilter === 'negative' ? 'text-red-600' : 'text-amber-600'}`} />
                {sentimentFilter} Comments
              </DialogTitle>
              <DialogDescription>
                All employee comments tagged as <strong className="capitalize">{sentimentFilter}</strong> by AI sentiment analysis, grouped by question.
              </DialogDescription>
            </DialogHeader>
            {(() => {
              if (!sentimentFilter || !commentsData?.questions) return null;
              const groups = commentsData.questions
                .map((q) => ({
                  question_text: q.question_text,
                  section: q.section,
                  matches: (q.comments_enriched || []).filter((c) => c.sentiment === sentimentFilter),
                }))
                .filter((g) => g.matches.length > 0);
              const totalMatches = groups.reduce((sum, g) => sum + g.matches.length, 0);
              const tone = sentimentFilter === 'positive' ? 'bg-green-50 border-green-200 text-green-900'
                          : sentimentFilter === 'negative' ? 'bg-red-50 border-red-200 text-red-900'
                          : 'bg-amber-50 border-amber-200 text-amber-900';
              return (
                <div className="overflow-y-auto pr-2 -mr-2 flex-1" data-testid="sentiment-dialog-body">
                  <p className="text-sm text-muted-foreground mb-4">
                    Showing <strong>{totalMatches}</strong> comment{totalMatches !== 1 ? 's' : ''} across {groups.length} question{groups.length !== 1 ? 's' : ''}.
                  </p>
                  {groups.length === 0 ? (
                    <div className="text-center py-8 text-sm text-muted-foreground italic">
                      No <span className="capitalize">{sentimentFilter}</span> comments found.
                    </div>
                  ) : (
                    <div className="space-y-5">
                      {groups.map((g, gi) => (
                        <div key={gi} data-testid={`sentiment-group-${gi}`}>
                          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{g.section}</p>
                          <p className="font-semibold text-sm mb-2">{g.question_text}</p>
                          <ul className="space-y-2">
                            {g.matches.map((c, ci) => (
                              <li
                                key={ci}
                                className={`border rounded-lg px-3 py-2 text-sm leading-snug ${tone}`}
                                data-testid={`sentiment-comment-${gi}-${ci}`}
                              >
                                {c.text}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default Analytics;
